
// Linear (no easing)
export const linear = (t: number) => t;

// Ease-out: fast start, slow end
export const easeOutQuad = (t: number) => t * (2 - t);
export const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

// Ease-in: slow start, fast end
export const easeInQuad = (t: number) => t * t;
export const easeInCubic = (t: number) => t ** 3;

// Ease-in-out: slow both ends
export const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
export const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t ** 3 : 1 - Math.pow(-2 * t + 2, 3) / 2);


/**
 * Animates a counter from 0 to target over duration with a chosen easing function.
 * @param target Final number.
 * @param durationMs Total time in milliseconds.
 * @param onUpdate Called with current value (0..target) at each frame.
 * @param easing Easing function (default linear).
 * @returns Promise that resolves when animation finishes.
 */
export function animateCounter(
  target: number,
  durationMs: number,
  onUpdate: (value: number) => void,
  easing: (t: number) => number = linear
): Promise<void> {
  return new Promise((resolve) => {
    const startTime = performance.now();
    const startValue = 0;

    const step = (now: number) => {
      const elapsed = now - startTime;
      let rawProgress = Math.min(1, elapsed / durationMs);
      // Apply easing to the linear progress
      let easedProgress = easing(rawProgress);
      let currentValue = startValue + (target - startValue) * easedProgress;

      onUpdate(currentValue);

      if (rawProgress < 1) {
        requestAnimationFrame(step);
      } else {
        onUpdate(target); // final exact value
        resolve();
      }
    };

    requestAnimationFrame(step);
  });
}
